import APIRequestsService from 'endpoint/api-requests-service.js';
import {ExceptionService, CountriesService} from '@busuu/legacy-core';
import SharedDataService from 'data/shared-data-service.js';
import endpoints from 'endpoint/endpoint-service.js';
import CurrentUser from 'user/current-user-service.js';
import QueryParameters from 'helpers/query-parameters.js';
import PostAuthenticationService from 'authentication/post-authentication-service.js';
import TranslationsService from 'common/translations.service.ts';
import MobileService from 'mobile/mobile-service.ts';
import {decomposeAxiosError, composeAxiosError} from 'endpoint/api-requests-service.helpers.js';
import {
    ERROR_BAD_RESPONSE_FORMAT,
    ERROR_PARAMS_MISSING,
    ERROR_TECHNICAL_ISSUE,
} from 'endpoint/api-requests-service.constants.js';

/*
 * Private vars
 */
const filename = 'registration-service.js';

const logError = (message, data) => {
    ExceptionService.handle('warning', {filename, message, data});
};

/**
 * Indicates whether or not a redirection needs to happen
 * based on the server side response.
 * @param  {Object} response
 * @return {String|null}
 */
const getBackendRedirect = (response) => {
    const redirectPresent = PostAuthenticationService.getBackendRedirectionLocation(response);
    return redirectPresent || null;
};

/**
 * Generate a random user's data
 * @param  {Object} response
 * @return {Object}
 */
const getRandomUserData = () => {
    const date = new Date();
    const dateComponants = [
        date.getFullYear(),
        date.getMonth() - 1,
        date.getDate(),
        date.getHours(),
        date.getMilliseconds(),
    ];
    const uniqueNumber = dateComponants.join('');
    const email = `Test+${uniqueNumber}@busuu.com`;
    const password = 'busuu123';
    const name = `User ${uniqueNumber}`;
    return {name, email, password};
};

/**
 * When we register in new users, we clean and set up the session by
 * removing session storage keys.
 * 1. Removes shared storage as we need to have a
 *    brand new version of storage (payments, datas shared, page views, etc.).
 * 2. Stores a key when a user just registered (used in webapp).
 * @private
 */
const setupSession = () => {
    SharedDataService.clearSpecificKeysAfterRegistration(); // 1
    SharedDataService.storeRegistrationOnboarding(); // 2;
};

/**
 * When a backend redirection is detected,
 * we mutate the data to pass it to the response back to the controllers.
 * 1. We also ensure that we clean and set up the session by
 * removing session storage keys.
 * @param {Object} response
 * @param {String} backendRedirection
 * @returns {Object}
 * @private
 */
const onBackendRedirection = (response, backendRedirection) => {
    response.data.backendRedirection = backendRedirection;
    setupSession(); // 1
    return {
        registrationResponse: response.data,
    };
};

/**
 * Retrieve marketing data from the session storage if any is present
 * and add it to the request params.
 */
const setCampaignParams = (params) => {
    const marketingData = SharedDataService.getMarketingData();
    if (marketingData) {
        params.origin = marketingData;
    }
};

/**
 * After registration is successful,
 * 1, 2. We set up a new session.
 * 3.    We then force the new user data to be fetched again on the session
 *       to ensure post operations that requires the user will provide the
 *       new user data (eg: Tracking).
 * @private
 */
const setupNewUser = () => {
    setupSession(); // 1, 2
    return CurrentUser.get({force: true}).catch(() => {}); // 3
};

/**
 * Register a new user on Busuu
 * @param {Object} params
 * @return {Promise}
 */
const createUser = (params) => {
    const onError = (error) => {
        const {applicationCode, message} = decomposeAxiosError(error, ERROR_TECHNICAL_ISSUE);

        logError(`createUser() error: ${applicationCode} - ${message}`, error);

        return Promise.reject({
            type: applicationCode,
            message,
        });
    };

    const onComplete = (response, user) => {
        return {
            registrationResponse: response.data,
            user,
        };
    };

    const onSuccess = (response) => {
        if (!response || !response.data) {
            return onError(composeAxiosError(ERROR_BAD_RESPONSE_FORMAT));
        }

        const backendRedirection = getBackendRedirect(response);
        if (backendRedirection) {
            return onBackendRedirection(response, backendRedirection);
        }

        return setupNewUser().then(onComplete.bind(null, response));
    };

    if (!params) {
        return onError(composeAxiosError(ERROR_PARAMS_MISSING));
    }

    setCampaignParams(params);

    const url = endpoints.getCaptchaBypassedEndpoint('register');
    return APIRequestsService.post(url, params).then(onSuccess).catch(onError);
};

/**
 * Returns a nonce token after a user is registered. This nonce token can
 * then be used to retrieve an access-token.
 * NB: This doesn't authenticate a user.
 * @param {Object} params
 * @return {Promise}
 */
const createUserAndGetNonce = (params) => {
    const onError = (error) => {
        const {applicationCode, message} = decomposeAxiosError(error, ERROR_TECHNICAL_ISSUE);

        logError(`createUserAndGetNonce() error: ${applicationCode} - ${message}`, error);

        return Promise.reject({
            type: applicationCode,
            message,
        });
    };

    const onSuccess = (response) => {
        if (!response) {
            return onError(composeAxiosError(ERROR_BAD_RESPONSE_FORMAT));
        }

        const backendRedirection = getBackendRedirect(response);
        if (response.data && backendRedirection) {
            return {
                nonce: null,
                backendRedirection,
            };
        }

        if (response.nonce) {
            return {
                nonce: response.nonce,
                backendRedirection: null,
            };
        }

        if (response?.data?.email_verification_required) {
            return {
                confirmation_link: response.data.tmp_link,
                email_verification_required: true,
            };
        }

        return onError(composeAxiosError(ERROR_BAD_RESPONSE_FORMAT));
    };

    if (!params) {
        return onError(composeAxiosError(ERROR_PARAMS_MISSING));
    }

    const url = endpoints.getCaptchaBypassedEndpoint('register');
    params.token_format = 'nonce';

    return APIRequestsService.post(url, params).then(onSuccess).catch(onError);
};

/**
 * Creates a new account based on FB user data.
 * @param {Object} params
 * @param {String} params.token
 * @param {String} params.interface_language
 * @param {String} params.speaking_language
 * @param {String} params.learning_language
 * @param {String} [params.country]
 */
const createFacebookUser = (params) => {
    const onError = (error) => {
        const {applicationCode, message} = decomposeAxiosError(error, ERROR_TECHNICAL_ISSUE);

        logError(`createFacebookUser() error: ${applicationCode} - ${message}`, error);

        return Promise.reject({
            type: applicationCode,
            message,
        });
    };

    const onComplete = (response, user) => {
        return {
            registrationResponse: response.data,
            user,
        };
    };

    const onSuccess = (response) => {
        if (!response || !response.data) {
            return onError(composeAxiosError(ERROR_BAD_RESPONSE_FORMAT));
        }

        const backendRedirection = getBackendRedirect(response);
        if (backendRedirection) {
            return onBackendRedirection(response, backendRedirection);
        }

        return setupNewUser().then(onComplete.bind(null, response));
    };

    if (!params) {
        return onError(composeAxiosError(ERROR_PARAMS_MISSING));
    }

    setCampaignParams(params);

    const url = endpoints.generateEndpoint('registerFacebook');
    return APIRequestsService.post(url, params).then(onSuccess).catch(onError);
};

/**
 * Creates a new account based on Google user data.
 * As the Busuu Authentication and Registration API is the same,
 * we always call the google authentication and when it's done,
 * we follow the normal flow for a new register user (get user and tracking).
 */
const createGoogleUser = (params) => {
    const onError = (error) => {
        const {applicationCode, message} = decomposeAxiosError(error, ERROR_TECHNICAL_ISSUE);

        logError(`createGoogleUser() error: ${applicationCode} - ${message}`, error);

        return Promise.reject({
            type: applicationCode,
            message,
        });
    };

    const onComplete = (response, user) => {
        return {
            registrationResponse: response.data,
            user,
        };
    };

    const onSuccess = (response) => {
        if (!response || !response.data) {
            return onError(composeAxiosError(ERROR_BAD_RESPONSE_FORMAT));
        }

        const backendRedirection = getBackendRedirect(response);
        if (backendRedirection) {
            return onBackendRedirection(response, backendRedirection);
        }

        return setupNewUser().then(onComplete.bind(null, response));
    };

    if (!params) {
        return onError(composeAxiosError(ERROR_PARAMS_MISSING));
    }

    setCampaignParams(params);

    const url = endpoints.generateEndpoint('registerGoogle');
    return APIRequestsService.post(url, params).then(onSuccess).catch(onError);
};

/**
 * Creates a new account based on Apple user data.
 * As the Busuu Authentication and Registration API is the same,
 * we always call the apple authentication and when it's done,
 * we follow the normal flow for a new register user (get user and tracking).
 */
const createAppleUser = (params) => {
    const onError = (error) => {
        const {applicationCode, message} = decomposeAxiosError(error, ERROR_TECHNICAL_ISSUE);

        logError(`createAppleUser() error: ${applicationCode} - ${message}`, error);

        return Promise.reject({
            type: applicationCode,
            message,
        });
    };

    const onComplete = (response, user) => {
        return {
            registrationResponse: response.data,
            user,
        };
    };

    const onSuccess = (response) => {
        if (!response || !response.data) {
            return onError(composeAxiosError(ERROR_BAD_RESPONSE_FORMAT));
        }

        const backendRedirection = getBackendRedirect(response);
        if (backendRedirection) {
            return onBackendRedirection(response, backendRedirection);
        }

        return setupNewUser().then(onComplete.bind(null, response));
    };

    if (!params) {
        return onError(composeAxiosError(ERROR_PARAMS_MISSING));
    }

    setCampaignParams(params);

    const url = endpoints.generateEndpoint('registerApple');

    return APIRequestsService.post(url, params).then(onSuccess).catch(onError);
};

/**
 * Returns a timezone string in the format:
 * region/city -> Ex: Europe/London
 * @return {String}
 */
const getTimezoneOffset = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; // TODO use future shared-js date/time package

    return timezone || null;
};

/**
 * Returns the URL to redirect to after
 * the registration is complete.
 * Because of the user premium state, the flow
 * can be different.
 * @param  {String} defaultURL
 * @param  {Object} registrationResponse
 * @param  {Object} user - optional
 */
const getAfterRegistrationURL = ({defaultURL, registrationResponse, user}) => {
    /**
     * When registration is for an institution,
     * we follow the email query parameter to the next step.
     */
    const params = QueryParameters.getInstitutionParameters();
    if (params.institution_code && user) {
        return defaultURL.replace('{email}', encodeURIComponent(user.email));
    }

    /**
     * If a backend redirection is present,
     * use that location instead
     */
    if (registrationResponse.backendRedirection) {
        return registrationResponse.backendRedirection;
    }

    return defaultURL;
};

/**
 * Returns the state of the 'opt in promotions' checkbox
 * Due to GDPR rules, we have to ensure that this checkbox
 * is unticked for EU countries but it can be ticked for
 * any other country.
 * The state can be overwritten by using a query parameter.
 * @return  {Boolean}
 */
const getOptInPromotionsState = (countryCode) => {
    const forceState = QueryParameters.getOptInParameter();
    switch (forceState) {
        case 'enabled':
            return true;
        case 'disabled':
            return false;
        default:
            return !CountriesService.isCountryGDPRProtected(countryCode);
    }
};

const submitVerification = (params) => {
    const onError = (error) => {
        const {applicationCode, message} = decomposeAxiosError(error, ERROR_TECHNICAL_ISSUE);

        logError(`submitVerification() error: ${applicationCode} - ${message}`, error);

        return Promise.reject({
            type: applicationCode,
            message,
        });
    };

    const onComplete = (response, user) => {
        return {
            registrationResponse: response.data,
            user,
        };
    };

    const onSuccess = (response) => {
        if (!response || !response.data) {
            return onError(composeAxiosError(ERROR_BAD_RESPONSE_FORMAT));
        }

        const backendRedirection = getBackendRedirect(response);
        if (backendRedirection) {
            return onBackendRedirection(response, backendRedirection);
        }

        return setupNewUser().then(onComplete.bind(null, response));
    };

    if (!params) {
        return onError(composeAxiosError(ERROR_PARAMS_MISSING));
    }

    setCampaignParams(params);

    const url = endpoints.generateEndpoint('registerVerification');
    return APIRequestsService.post(url, params).then(onSuccess).catch(onError);
};

/**
 * Returns a translated error message based on the error type
 * @param {String} type
 */
const getErrorMessage = (type) => {
    switch (type) {
        case 'EMAIL_BLANK':
        case 'NAME_BLANK':
            return TranslationsService.getTranslation('MISSED_SOMETHING');
        case 'PASSWORD_BLANK':
            return TranslationsService.getAndReplaceTranslation('PASSWORD_MIN_CHARACTERS', '6');
        case 'CODE_ACTIVATED': // Institutions
            return TranslationsService.getTranslation('LOOKS_LIKE_YOURE_ALREADY_REGISTERED_PLEASE_USE');
        case 'CODE_NOT_FOUND': // Institutions
        case 'CODE_ACTIVATION_ERROR':
            return TranslationsService.getTranslation('SORRY_YOUR_ARE_NOT_AUTHORISED_TO_USE_THIS_APP_PLEASE_CONTACT_X');
        case 'EMAIL_INVALID':
            return TranslationsService.getTranslation('EMAIL_UNVALID');
        case 'NAME_TOO_LONG':
            return TranslationsService.getTranslation('NAME_TOO_LONG');
        default:
            return `${TranslationsService.getTranslation('ERROR')} - ${TranslationsService.getTranslation(
                'TRY_AGAIN'
            )}`;
    }
};

/**
 * Returns if the user can be opted in promotions based on if:
 * - toggle element has been disabled/enabled
 * - promotions have been disabled/enabled from the query params
 * - their country is GDPR protected
 * @param {String} countryCode
 * @param {Object} elem - optional
 */
const canOptInPromotions = (countryCode, elem = null) => {
    const optInPromotionsQueryParam = QueryParameters.getOptInParameter();
    const isInGDPRCompliantCountry = CountriesService.isCountryGDPRProtected(countryCode);

    if (elem && elem.checked) return true;

    if (optInPromotionsQueryParam === 'enabled') return true;

    if (optInPromotionsQueryParam === 'disabled') return false;

    if (!isInGDPRCompliantCountry) return true;

    return false;
};

const getPlatformSource = () => {
    const mobileAuthParameter = QueryParameters.getMobileAuthParameter();
    const userComesFromMobile = MobileService.isAuthenticationOrRegistrationMobileFlow();
    const defaultPlatform = 'web';

    return userComesFromMobile ? mobileAuthParameter : defaultPlatform;
};

// TEMPORARY while a high enough number of Android users don't update the app
// we force UK English interface for all the users outside of US during registration
const getInterfaceLanguage = (languageCode, countryCode) => {
    if (languageCode === 'en-US' && countryCode !== 'US') return 'en';
    return languageCode;
};

const RegistrationService = {
    createUser,
    createUserAndGetNonce,
    createFacebookUser,
    createGoogleUser,
    createAppleUser,
    getTimezoneOffset,
    getAfterRegistrationURL,
    getOptInPromotionsState,
    submitVerification,
    getErrorMessage,
    canOptInPromotions,
    getRandomUserData,
    getPlatformSource,
    getInterfaceLanguage,
};

export default RegistrationService;
